import { AppModuleCode, PermissionCode, ProductCode, type AuthUser } from '@gb-ticket/shared-types';
import { BookOpenIcon, CircleHelpIcon, HouseIcon, TicketIcon } from 'lucide-react';
import { useLocation } from 'react-router';

import { hasPermission, isModuleEnabled } from '@/features/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { ProductSideNav, type ProductSideNavItem } from '@/platform/components/product-side-nav';
import { registerProductNav } from '@/platform/lib/product-nav';
import { agentNavGroups } from '@/products/helpdesk/features/nav/agent-nav';
import { AgentNavList } from '@/products/helpdesk/features/nav/agent-nav-list';
import {
  isPortalAudience,
  isPortalPath,
  portalNavItems,
  PORTAL_ROOT,
} from '@/products/helpdesk/features/portal/portal-paths';
import { HELPDESK_ROOT } from '@/products/helpdesk/helpdesk-paths';

const portalIcons: Record<string, ProductSideNavItem['icon']> = {
  [PORTAL_ROOT]: <HouseIcon aria-hidden className="size-4" strokeWidth={1.75} />,
  [`${PORTAL_ROOT}/tickets`]: <TicketIcon aria-hidden className="size-4" strokeWidth={1.75} />,
  [`${PORTAL_ROOT}/wissen`]: <BookOpenIcon aria-hidden className="size-4" strokeWidth={1.75} />,
  [`${PORTAL_ROOT}/faq`]: <CircleHelpIcon aria-hidden className="size-4" strokeWidth={1.75} />,
};

function HelpdeskNav() {
  const me = useMe();
  const location = useLocation();

  if (!me.data) {
    return null;
  }

  if (isPortalPath(location.pathname)) {
    return (
      <ProductSideNav
        ariaLabel="Hilfe-Portal"
        items={portalNavItems(me.data).map((item) => ({
          ...item,
          icon: portalIcons[item.to],
        }))}
      />
    );
  }

  return <AgentNavList groups={agentNavGroups(me.data)} />;
}

function helpdeskMenuTilesFor(user: AuthUser) {
  if (isPortalAudience(user)) {
    return portalNavItems(user).map((item) => ({
      id: `portal-${item.to}`,
      label: item.label,
      to: item.to,
    }));
  }
  return agentNavGroups(user).flatMap((group) =>
    group.items.map((item) => ({
      id: item.to,
      label: item.label,
      to: item.to,
      group: group.id === 'work' ? 'Arbeit' : (group.label ?? undefined),
    })),
  );
}

registerProductNav({
  code: ProductCode.HELPDESK,
  Nav: HelpdeskNav,
  menuTilesFor: helpdeskMenuTilesFor,
  mobileSlots: [
    {
      kind: 'link',
      id: 'helpdesk-tickets',
      label: 'Tickets',
      to: '/helpdesk/tickets',
      isVisible: (user) =>
        hasPermission(user, PermissionCode.TICKETS_READ_ALL) &&
        hasPermission(user, PermissionCode.TICKETS_READ),
    },
    {
      kind: 'link',
      id: 'helpdesk-overview',
      label: 'Übersicht',
      to: HELPDESK_ROOT,
      isVisible: (user) => hasPermission(user, PermissionCode.TICKETS_READ_ALL),
    },
    {
      kind: 'link',
      id: 'portal-tickets',
      label: 'Tickets',
      to: `${PORTAL_ROOT}/tickets`,
      isVisible: (user) =>
        !hasPermission(user, PermissionCode.TICKETS_READ_ALL) &&
        hasPermission(user, PermissionCode.TICKETS_READ),
    },
    {
      kind: 'link',
      id: 'portal-overview',
      label: 'Übersicht',
      to: PORTAL_ROOT,
      isVisible: (user) => !hasPermission(user, PermissionCode.TICKETS_READ_ALL),
    },
    {
      kind: 'link',
      id: 'helpdesk-wissen',
      label: 'Wissen',
      to: '/helpdesk/wissen',
      isVisible: (user) =>
        hasPermission(user, PermissionCode.TICKETS_READ_ALL) &&
        hasPermission(user, PermissionCode.KB_READ) &&
        isModuleEnabled(user, AppModuleCode.KNOWLEDGE),
    },
    {
      kind: 'link',
      id: 'portal-wissen',
      label: 'Wissen',
      to: `${PORTAL_ROOT}/wissen`,
      isVisible: (user) =>
        !hasPermission(user, PermissionCode.TICKETS_READ_ALL) &&
        hasPermission(user, PermissionCode.KB_READ) &&
        isModuleEnabled(user, AppModuleCode.KNOWLEDGE),
    },
  ],
});
